import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api, withOptionalCompanyId } from '@/shared/api/base';
import type {
  MeetingGroupCreateRequest,
  MeetingGroupListResponse,
  MeetingGroupMemberAddRequest,
  MeetingGroupUpdateRequest,
} from '@/shared/api/types/schema.ts';
import { useAuthBootstrap } from '@/shared/lib';

export type MeetingGroupAudioUploadAcceptedResponse = {
  upload_job_id: string;
  conversation_id: string;
};

export type MeetingGroupUploadJobStatusResponse = {
  status?: string;
  conversation_id?: string;
  detail?: string;
  error?: string;
};

const keys = {
  root: () => ['meeting-groups'],
  all: () => [...keys.root(), 'all'],
  single: (meetingGroupById: string, companyId?: string | null) => [
    ...keys.root(),
    meetingGroupById,
    'single',
    companyId ?? null,
  ],
  uploadJob: (meetingGroupById: string, uploadJobId: string, companyId?: string | null) => [
    ...keys.root(),
    meetingGroupById,
    'uploads',
    uploadJobId,
    companyId ?? null,
  ],
};

export const useGetMeetingGroups = (companyId?: string | null) => {
  const { hasToken } = useAuthBootstrap();
  return useQuery({
    queryKey: [...keys.all(), companyId],
    queryFn: async () => {
      const res = await api.instance.get<MeetingGroupListResponse>('/meeting-groups', {
        params: { company_id: companyId || undefined },
      });
      return res.data;
    },
    // Запрос выполняется только если есть токен И выбрана компания
    // Это предотвращает race condition при инициализации
    enabled: hasToken && !!companyId,
    staleTime: 30_000, // 30 seconds
  });
};

export const useGetMeetingGroupById = (meetingGroupById: string, companyId?: string | null) => {
  return useQuery({
    queryKey: keys.single(meetingGroupById, companyId),
    queryFn: async () => {
      const res = await api.meetingGroups.getMeetingGroupMeetingGroupsMeetingGroupIdGet(
        meetingGroupById,
        withOptionalCompanyId(companyId),
      );
      return res.data;
    },
    enabled: Boolean(meetingGroupById),
    refetchOnMount: 'always',
  });
};

export const useAddMeetingGroupMutation = (companyId?: string | null) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: MeetingGroupCreateRequest) => {
      if (!companyId) {
        throw new Error('companyId is required to create meeting group');
      }
      return api.meetingGroups.createMeetingGroupMeetingGroupsPost(data, {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        //@ts-expect-error
        params: { company_id: companyId },
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: keys.all() });
    },
  });
};

export const useUpdateMeetingGroupMutation = (
  meetingGroupId: string,
  companyId?: string | null,
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: MeetingGroupUpdateRequest) =>
      api.meetingGroups.updateMeetingGroupMeetingGroupsMeetingGroupIdPatch(
        meetingGroupId,
        data,
        withOptionalCompanyId(companyId),
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: keys.root() });
    },
  });
};

export const useAddMeetingGroupMemberMutation = (
  meetingGroupId: string,
  companyId?: string | null,
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: MeetingGroupMemberAddRequest) =>
      api.meetingGroups.addMeetingGroupMemberMeetingGroupsMeetingGroupIdMembersPost(
        meetingGroupId,
        data,
        withOptionalCompanyId(companyId),
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: keys.root() });
    },
  });
};

export const useRemoveMeetingGroupMemberMutation = (
  meetingGroupId: string,
  companyId?: string | null,
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) =>
      api.meetingGroups.removeMeetingGroupMemberMeetingGroupsMeetingGroupIdMembersUserIdDelete(
        meetingGroupId,
        userId,
        withOptionalCompanyId(companyId),
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: keys.root() });
    },
  });
};

export const useUpdateMeetingGroupMemberRoleMutation = (
  meetingGroupId: string,
  companyId?: string | null,
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, role }: { userId: string; role: string }) =>
      api.meetingGroups.updateMeetingGroupMemberRoleMeetingGroupsMeetingGroupIdMembersUserIdPatch(
        meetingGroupId,
        userId,
        { role },
        withOptionalCompanyId(companyId),
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: keys.root() });
    },
  });
};

export const useUploadMeetingGroupAudioMutation = (meetingGroupId: string) => {
  return useMutation({
    mutationFn: (file: File) => {
      const formData = new FormData();
      formData.append('audio_file', file);

      return api.meetingGroups.uploadMeetingGroupAudioMeetingGroupsMeetingGroupIdUploadsAudioPost(
        meetingGroupId,
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        //@ts-expect-error
        formData,
      );
    },
  });
};

export const useGetMeetingGroupUploadJob = (
  meetingGroupId: string,
  uploadJobId: string,
  companyId?: string | null,
  enabled: boolean = true,
) => {
  return useQuery({
    queryKey:
      uploadJobId != null
        ? keys.uploadJob(meetingGroupId, uploadJobId, companyId)
        : [...keys.root(), meetingGroupId, 'uploads', 'none', companyId ?? null],
    queryFn: async () => {
      const res =
        await api.meetingGroups.getMeetingGroupAudioUploadStatusMeetingGroupsMeetingGroupIdUploadsUploadJobIdGet(
          meetingGroupId,
          uploadJobId,
        );
      return res.data;
    },
    enabled: Boolean(meetingGroupId) && Boolean(uploadJobId) && enabled,
    refetchInterval: (query) => {
      const status = (query.state.data as MeetingGroupUploadJobStatusResponse | undefined)?.status;
      const normalized = typeof status === 'string' ? status.toLowerCase() : '';

      const isTerminal = [
        'completed',
        'complete',
        'done',
        'success',
        'failed',
        'error',
        'canceled',
        'cancelled',
      ].includes(normalized);

      return isTerminal ? false : 3000;
    },
  });
};

export const useDeleteMeetingGroupMutation = (
  meetingGroupId: string,
  companyId?: string | null,
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api.meetingGroups.deleteMeetingGroupMeetingGroupsMeetingGroupIdDelete(
        meetingGroupId,
        withOptionalCompanyId(companyId),
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: keys.all() });
    },
  });
};
